import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { usePlayerCard } from "@/context/PlayerCardContext";
import Avatar from "@/components/Avatar";
import { Search, Loader2, Crown } from "lucide-react";

export default function PlayerSearchBar() {
  const { openPlayerCard } = usePlayerCard();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/members/search?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => setResults(data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (r) => { setOpen(false); setQ(""); setResults([]); openPlayerCard(r.id); };

  return (
    <div ref={boxRef} className="relative z-30 flex justify-end mb-5" data-testid="player-search-bar">
      <div className={`relative w-full transition-all duration-300 ${open || q ? "max-w-sm" : "max-w-[220px]"}`}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
        <input data-testid="player-search-input" value={q} onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Find a player…" aria-label="Find a player"
          className="w-full bg-pb-surface/80 backdrop-blur border border-pb-border rounded-full pl-9 pr-8 py-2 text-xs text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-pb-red/60 focus:bg-pb-surface transition-colors" />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-pb-red animate-spin" />}
        {open && q.trim().length >= 2 && (
          <div className="absolute top-full right-0 mt-2 w-full bg-pb-surface border border-pb-border shadow-2xl shadow-black/60 overflow-hidden animate-fade-up" data-testid="player-search-results">
            {results.length === 0 && !loading ? (
              <div className="px-4 py-3 text-[11px] text-zinc-500">No players match "{q.trim()}"</div>
            ) : results.map((r) => (
              <button key={r.id} data-testid={`player-search-result-${r.id}`} onClick={() => pick(r)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-pb-elevated transition-colors">
                <Avatar src={r.avatar} name={r.name} size={26} />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1 text-xs font-semibold truncate">
                    {r.membership === "vip" && <Crown className="w-3 h-3 text-pb-red shrink-0" />}{r.name}
                  </span>
                  {r.location && <span className="block text-[9px] uppercase tracking-widest text-zinc-500 truncate">{r.location}</span>}
                </span>
                <span className="shrink-0 text-[8px] font-black uppercase tracking-widest text-zinc-600">View</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
